import { useEffect, useState } from "react";
import { useRouter } from "next/router";

import { gql } from '@apollo/client';
import { useFormik } from "formik";
import { useToast } from "@netfront/ekardo-content-library";
import { IUser, useGetLoggedUser, useLoggedGeladaMutation } from "@netfront/gelada-identity-library";
import { Button, Input } from "@netfront/ui-library";
import * as yup from 'yup';

import { IVerifyPhoneForm } from "./VerifyPhoneForm.interfaces";
import { getCookieValue } from "@netfront/common-library";
import { CONSTANTS } from "@/constants";
import { useAddCustomEvent } from "@/hooks";
import { ensureAustralianAreaCode } from "@/utils";

export const VerifyPhoneForm = () => {
  const { push } = useRouter();
  const { handleToastError } = useToast();
  const { handleAddCustomEvent } = useAddCustomEvent();

  const [user, setUser] = useState<IUser>();
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);

  const phoneNumberCookie = getCookieValue(CONSTANTS.COOKIES.PHONE_NUMBER);

  const { handleGetLoggedUser } = useGetLoggedUser({
    onCompleted: ({ user }) => {
      setUser(user);
    }
  });

  const [updatePhoneNumber] = useLoggedGeladaMutation({
    mutation: gql`
      mutation($phoneNumber: String!) {
        user {
          updatePhoneNumber(phoneNumber: $phoneNumber)
        }
      }
    `,
    options: {
      onCompleted: () => {
        handleAddCustomEvent({ 
          projectId: String(process.env.REACT_APP_PROJECT_ID), 
          type: `Phone verification: token requested` 
        });
        setIsFormSubmitting(false);
        push('/user/verify-phone-code');
      },
      onError: (error) => {
        handleToastError({
          error,
          shouldUseFriendlyErrorMessage: true,
        });
        setIsFormSubmitting(false);
      },
    }
  });


  const formik = useFormik<IVerifyPhoneForm>({
    initialValues: {
      phoneNumber: '',
    },
    validationSchema: yup.object().shape({
      phoneNumber: yup
          .string()
          .transform((value) => value.replace(/\s+/g, ''))
          .matches(
            /^(\+61[2-478]\d{8}|0[2-478]\d{8})$/,
            'Please enter a valid Australian phone number'
          ),

    }),
    onSubmit: async () => {
      setIsFormSubmitting(true);

      updatePhoneNumber({ 
        variables: { 
          phoneNumber: ensureAustralianAreaCode(values.phoneNumber) 
        } 
      });
    },
  });

  useEffect(() => {
    handleGetLoggedUser();
  }, [])

  useEffect(() => {
    if(phoneNumberCookie) {
      formik.setFieldValue('phoneNumber', getCookieValue(CONSTANTS.COOKIES.PHONE_NUMBER));
      return;
    }

    if(user?.credential.phoneNumber) {
      formik.setFieldValue('phoneNumber', user.credential.phoneNumber);
      return;
    }
  }, [phoneNumberCookie, user]);

  const { errors, touched, handleBlur, handleChange, handleSubmit, values } = formik;

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="flex flex-wrap -mx-4">
        <div className="w-full px-4">
          <Input
            errorMessage={touched.phoneNumber && errors.phoneNumber ? errors.phoneNumber : ''}
            id="phoneNumber"
            labelText="Phone number"
            name="phoneNumber"
            placeholder="Enter your phone number"
            type="text"
            value={values.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
      </div>

      <Button
        additionalClassNames="w-full"
        text={'Verify my phone number'}
        type="submit"
        isLoading={isFormSubmitting}
      />
    </form>
  );
};